import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Check, Search, ChevronDown, Loader2, Plus, Trash2, Lock } from 'lucide-react';
import { cn } from '../lib/utils';
import { teamsAPI, trainingAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useTeam } from '../context/TeamContext';
import PremiumGateOverlay from './PremiumGateOverlay';

const PlayerStatsModal = ({ session, video, isOpen, onClose, onSuccess }) => {
    const { toast } = useToast();
    const { plan } = useTeam();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [roster, setRoster] = useState([]);
    const [stats, setStats] = useState([]);
    const [statOpen, setStatOpen] = useState(false);
    const [playerSearch, setPlayerSearch] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    // New Stat Form State
    const [selectedPlayer, setSelectedPlayer] = useState('');
    const [statType, setStatType] = useState('passing');
    const [statValue, setStatValue] = useState('');

    const filteredRoster = roster.filter(player =>
        (player.username || '').toLowerCase().includes(playerSearch.toLowerCase())
    );

    // Stat Categories
    const OFFENSIVE_STATS = [
        { value: 'passing', label: 'Passing Yds' },
        { value: 'rushing', label: 'Rushing Yds' },
        { value: 'receiving', label: 'Receiving Yds' }
    ];

    const DEFENSIVE_STATS = [
        { value: 'tackles', label: 'Tackles' },
        { value: 'tackle_for_loss', label: 'Tackle for Loss' },
        { value: 'sacks', label: 'Sacks' },
        { value: 'interceptions', label: 'Interceptions' },
        { value: 'pass_deflections', label: 'Pass Deflections' },
        { value: 'forced_fumbles', label: 'Forced Fumbles' }
    ];

    // Determine available stats based on selected player's position
    const getAvailableStats = () => {
        const result = { offense: [], defense: [] };
        const player = selectedPlayer ? roster.find(p => p.user_id === selectedPlayer) : null;

        if (!player) return result;

        const positions = (player.positions || []).map(p => p.toUpperCase());
        const isOffense = positions.some(pos => ['QB', 'RB', 'WR', 'TE', 'OL', 'C', 'G', 'T', 'FB'].includes(pos));
        const isDefense = positions.some(pos => ['DL', 'LB', 'DB', 'CB', 'S', 'DE', 'DT', 'NT', 'FS', 'SS', 'MLB', 'OLB'].includes(pos));

        if (isOffense) {
            // Fine-grained offensive filtering
            const stats = [];

            // Passing: QB only
            if (positions.includes('QB')) {
                stats.push(OFFENSIVE_STATS.find(s => s.value === 'passing'));
            }

            // Rushing: QB, RB, WR, FB
            if (positions.some(pos => ['QB', 'RB', 'WR', 'FB'].includes(pos))) {
                stats.push(OFFENSIVE_STATS.find(s => s.value === 'rushing'));
            }

            // Receiving: RB, WR, TE, FB
            if (positions.some(pos => ['RB', 'WR', 'TE', 'FB'].includes(pos))) {
                stats.push(OFFENSIVE_STATS.find(s => s.value === 'receiving'));
            }

            result.offense = stats.filter(Boolean);
        }

        if (isDefense) {
            result.defense = DEFENSIVE_STATS;
        }

        return result;
    };

    useEffect(() => {
        if (isOpen && session?.team_id) {
            fetchRoster();
        }
        if (isOpen && video) {
            // Robustly handle play_data
            let ps = [];
            let pd = video.play_data;

            // Handle stringified JSON case (defensive)
            if (typeof pd === 'string') {
                try {
                    pd = JSON.parse(pd);
                } catch (e) {
                    console.error("Failed to parse play_data:", e);
                    pd = {};
                }
            }

            if (pd && pd.player_stats) {
                ps = pd.player_stats;
            }

            setStats(ps);
        }
    }, [isOpen, session, video]);

    const fetchRoster = async () => {
        setLoading(true);
        try {
            const res = await teamsAPI.getMembers(session.team_id);
            // Filter active players ONLY (exclude coaches)
            // m.role is 'player' or 'coach'
            const players = res.data.filter(m => m.status === 'active' && m.role === 'player');
            setRoster(players);
        } catch (error) {
            console.error("Failed to fetch roster:", error);
            toast({
                title: "Error",
                description: "Failed to load team roster.",
                variant: "destructive"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleAddStat = () => {
        if (!selectedPlayer || !statType) return;

        // Defensive stats default to 1 and don't require value input
        const isDefensive = DEFENSIVE_STATS.some(s => s.value === statType);

        if (!isDefensive && !statValue) return;

        // Backend response is flat: m.user_id, m.username
        const player = roster.find(p => p.user_id === selectedPlayer);
        const intValue = isDefensive ? 1 : (parseInt(statValue, 10) || 0);

        const newStatsToAdd = [];

        // 1. Add the primary stat
        newStatsToAdd.push({
            player_id: selectedPlayer,
            player_name: player ? (player.username || 'Unknown') : 'Unknown',
            stat_type: statType,
            value: intValue
        });

        // 2. Auto-add Touchdown if result is Touchdown and stat is yardage
        const isTouchdownPlay = video.play_data?.result?.toLowerCase() === 'touchdown';
        const isYardageStat = ['rushing', 'receiving', 'interceptions'].includes(statType);

        if (isTouchdownPlay && isYardageStat) {
            // Avoid duplicate touchdown if already added
            const alreadyHasTD = stats.some(s => s.player_id === selectedPlayer && s.stat_type === 'touchdowns') ||
                newStatsToAdd.some(s => s.stat_type === 'touchdowns');

            if (!alreadyHasTD) {
                newStatsToAdd.push({
                    player_id: selectedPlayer,
                    player_name: player ? (player.username || 'Unknown') : 'Unknown',
                    stat_type: 'touchdowns',
                    value: 1
                });
                toast({ title: "Auto-added Touchdown stat!" });
            }
        }

        setStats([...stats, ...newStatsToAdd]);

        // Reset form
        setSelectedPlayer('');
        setStatValue('');
    };

    const handleRemoveStat = (index) => {
        const newStats = [...stats];
        newStats.splice(index, 1);
        setStats(newStats);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            // Check for pending inputs (User forgot to click Add)
            let statsToSave = [...stats];

            if (selectedPlayer && statType) {
                // Defensive default or value presence
                const isDefensive = DEFENSIVE_STATS.some(s => s.value === statType);
                if (isDefensive || statValue) {
                    // Replicate handleAddStat logic
                    const player = roster.find(p => p.user_id === selectedPlayer);
                    const intValue = isDefensive ? 1 : (parseInt(statValue, 10) || 0);

                    statsToSave.push({
                        player_id: selectedPlayer,
                        player_name: player ? (player.username || 'Unknown') : 'Unknown',
                        stat_type: statType,
                        value: intValue
                    });

                    // Handle auto-touchdown logic if needed? 
                    // For simplicity, skip complex auto-TD logic here or copy it.
                    // The user will see it added next time.
                }
            }

            // Construct the update payload
            await trainingAPI.updateVideo(session.id, video.id, {
                player_stats: statsToSave
            });

            toast({ title: "Stats saved successfully" });
            onSuccess(); // Refresh parent
            onClose();
        } catch (error) {
            console.error("Failed to save stats:", error);
            toast({
                title: "Save Failed",
                description: "Could not save player stats.",
                variant: "destructive"
            });
        } finally {
            setSaving(false);
        }
    };

    const isDefensiveSelected = DEFENSIVE_STATS.some(s => s.value === statType);

    const isFreePlan = plan !== 'premium';

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className={`${isFreePlan ? 'p-0 overflow-hidden bg-[#0a0a0b] border-gray-800 sm:max-w-[800px]' : 'bg-[#1a1a1b] border-gray-800 sm:max-w-[600px]'} text-white`}>
                {!isFreePlan && (
                    <DialogHeader>
                        <DialogTitle>Player Stats</DialogTitle>
                        <DialogDescription className="text-gray-400 text-sm">
                            Assign individual player stats to this play based on performance.
                        </DialogDescription>
                    </DialogHeader>
                )}

                {isFreePlan ? (
                    <div className="relative w-full h-[750px] flex flex-col">
                        <PremiumGateOverlay onClose={onClose} />
                    </div>
                ) : (
                    <div className="space-y-6 py-4 relative min-h-[400px]">
                        {/* Add New Stat Section */}
                        <div className="bg-[#0a0a0b] p-4 rounded-lg border border-gray-800 space-y-4">
                            <h4 className="text-sm font-medium text-gray-400">Add Player Stat</h4>
                            <div className="grid grid-cols-12 gap-3 items-end">
                                <div className="col-span-5 space-y-2">
                                    <Label className="text-xs">Player</Label>
                                    <Popover open={statOpen} onOpenChange={setStatOpen}>
                                        <PopoverTrigger asChild>
                                            <div className="relative">
                                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
                                                <Input
                                                    placeholder="Search Player..."
                                                    value={isFocused ? playerSearch : (roster.find(p => p.user_id === selectedPlayer)?.username || playerSearch)}
                                                    onChange={(e) => {
                                                        setPlayerSearch(e.target.value);
                                                        setStatOpen(true);
                                                        if (selectedPlayer) setSelectedPlayer('');
                                                    }}
                                                    onFocus={() => {
                                                        setIsFocused(true);
                                                        setStatOpen(true);
                                                        setPlayerSearch(''); // Clear to search
                                                    }}
                                                    onBlur={() => {
                                                        setIsFocused(false);
                                                        // Small timeout to allow click to register on CommandItem before list hides
                                                        setTimeout(() => {
                                                            setPlayerSearch('');
                                                        }, 200);
                                                    }}
                                                    className="pl-8 bg-[#1a1a1b] border-gray-700 h-9 text-xs text-white placeholder:text-gray-600 focus:ring-1 focus:ring-blue-500/50"
                                                />
                                                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500 opacity-50 pointer-events-none" />
                                            </div>
                                        </PopoverTrigger>
                                        <PopoverContent
                                            className="w-[300px] p-0 bg-[#0a0a0b] border-gray-800 z-[110] shadow-2xl"
                                            align="start"
                                            onOpenAutoFocus={(e) => e.preventDefault()} // Don't steal focus from input
                                        >
                                            <Command className="bg-transparent" shouldFilter={false}>
                                                <CommandList className="max-h-[250px] scrollbar-thin">
                                                    <CommandEmpty className="py-4 text-xs text-gray-500">No player found.</CommandEmpty>
                                                    <CommandGroup>
                                                        {filteredRoster.map((member) => (
                                                            <CommandItem
                                                                key={member.user_id}
                                                                value={member.username}
                                                                onSelect={() => {
                                                                    setSelectedPlayer(member.user_id);
                                                                    setPlayerSearch(''); // Hide text after select
                                                                    setStatOpen(false);
                                                                }}
                                                                className="text-white hover:bg-white/10 cursor-pointer py-2 px-3 data-[selected=true]:bg-white/10"
                                                            >
                                                                <div className="flex items-center justify-between w-full">
                                                                    <div className="flex items-center gap-2">
                                                                        <div className="h-6 w-6 rounded-full bg-blue-600/20 text-blue-400 flex items-center justify-center text-[10px] font-bold border border-blue-600/30">
                                                                            {member.username.substring(0, 2).toUpperCase()}
                                                                        </div>
                                                                        <div>
                                                                            <div className="text-xs font-medium">{member.username}</div>
                                                                            {member.positions && member.positions.length > 0 && (
                                                                                <div className="text-[10px] text-gray-500">{member.positions}</div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    {selectedPlayer === member.user_id && (
                                                                        <Check className="h-3.5 w-3.5 text-blue-400" />
                                                                    )}
                                                                </div>
                                                            </CommandItem>
                                                        ))}
                                                    </CommandGroup>
                                                </CommandList>
                                            </Command>
                                        </PopoverContent>
                                    </Popover>
                                </div>

                                <div className={isDefensiveSelected ? "col-span-6 space-y-2" : "col-span-4 space-y-2"}>
                                    <Label className="text-xs">Stat Type</Label>
                                    <Select value={statType} onValueChange={setStatType}>
                                        <SelectTrigger className="bg-[#1a1a1b] border-gray-700 h-9 text-xs">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {(() => {
                                                const { offense, defense } = getAvailableStats();
                                                return (
                                                    <>
                                                        {offense.length > 0 && (
                                                            <SelectGroup>
                                                                <SelectLabel className="text-blue-400 text-[10px] uppercase font-bold px-2 py-1 mt-1">Offensive Stats</SelectLabel>
                                                                {offense.map(s => (
                                                                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                                                ))}
                                                            </SelectGroup>
                                                        )}
                                                        {offense.length > 0 && defense.length > 0 && (
                                                            <SelectSeparator className="bg-gray-800" />
                                                        )}
                                                        {defense.length > 0 && (
                                                            <SelectGroup>
                                                                <SelectLabel className="text-red-400 text-[10px] uppercase font-bold px-2 py-1 mt-1">Defensive Stats</SelectLabel>
                                                                {defense.map(s => (
                                                                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                                                ))}
                                                            </SelectGroup>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {!isDefensiveSelected && (
                                    <div className="col-span-2 space-y-2">
                                        <Label className="text-xs">Value</Label>
                                        <Input
                                            type="number"
                                            value={statValue}
                                            onChange={(e) => setStatValue(e.target.value)}
                                            className="bg-[#1a1a1b] border-gray-700 h-9 text-xs"
                                            placeholder="0"
                                        />
                                    </div>
                                )}

                                <div className="col-span-1">
                                    <Button
                                        size="sm"
                                        className="h-9 w-full bg-blue-600 hover:bg-blue-700 p-0"
                                        onClick={handleAddStat}
                                        disabled={!selectedPlayer || (!isDefensiveSelected && !statValue)}
                                    >
                                        <Plus className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* Existing Stats List grouped by Category */}
                        <div className="space-y-4">
                            <Label className="text-gray-400 text-xs uppercase tracking-wider">Assigned Stats</Label>

                            {stats.length === 0 ? (
                                <div className="text-center py-6 text-gray-500 text-sm italic bg-[#0a0a0b]/50 rounded border border-dashed border-gray-800">
                                    No stats assigned to this clip yet.
                                </div>
                            ) : (
                                <div className="space-y-4 max-h-[300px] overflow-y-auto pr-1">
                                    {/* Offensive Group */}
                                    {stats.some(s => OFFENSIVE_STATS.some(os => os.value === s.stat_type)) && (
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2">
                                                <div className="h-px flex-1 bg-blue-900/30"></div>
                                                <span className="text-[10px] font-bold text-blue-400 uppercase">Offense</span>
                                                <div className="h-px flex-1 bg-blue-900/30"></div>
                                            </div>
                                            {stats.map((stat, i) => OFFENSIVE_STATS.some(os => os.value === stat.stat_type) && (
                                                <StatItem key={i} stat={stat} index={i} onRemove={handleRemoveStat} />
                                            ))}
                                        </div>
                                    )}

                                    {/* Defensive Group */}
                                    {stats.some(s => DEFENSIVE_STATS.some(ds => ds.value === s.stat_type)) && (
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2">
                                                <div className="h-px flex-1 bg-red-900/30"></div>
                                                <span className="text-[10px] font-bold text-red-400 uppercase">Defense</span>
                                                <div className="h-px flex-1 bg-red-900/30"></div>
                                            </div>
                                            {stats.map((stat, i) => DEFENSIVE_STATS.some(ds => ds.value === stat.stat_type) && (
                                                <StatItem key={i} stat={stat} index={i} onRemove={handleRemoveStat} />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {!isFreePlan && (
                    <DialogFooter>
                        <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
                        <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : 'Save Stats'}
                        </Button>
                    </DialogFooter>
                )}
            </DialogContent >
        </Dialog >
    );
};

const StatItem = ({ stat, index, onRemove }) => (
    <div className="flex items-center justify-between p-2 bg-[#0a0a0b] border border-gray-800 rounded text-sm">
        <div className="flex items-center gap-3">
            <div className="h-6 w-6 rounded-full bg-blue-900/50 flex items-center justify-center text-xs text-blue-400 font-bold">
                {stat.player_name.charAt(0)}
            </div>
            <div>
                <span className="text-white block text-xs">{stat.player_name}</span>
                <span className="text-gray-500 text-[10px] capitalize">{stat.stat_type.replace('_', ' ')}: {stat.value}</span>
            </div>
        </div>
        <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-gray-500 hover:text-red-500 hover:bg-red-500/10"
            onClick={() => onRemove(index)}
        >
            <Trash2 className="w-3 h-3" />
        </Button>
    </div>
);

export default PlayerStatsModal;
